// SSE 连接状态
enum CONNECTION_STATUS {
  CONNECTING,
  OPEN,
  CLOSED,
}

// SSE 客户端类
export class SSEClient {
  private eventSource!: EventSource;

  private url: string;

  status: CONNECTION_STATUS = CONNECTION_STATUS.CLOSED;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private messageHandle: (message: any) => void = () => {};

  private errorHandle: (error: Event) => void = () => {};

  private openHandle: () => void = () => {};

  constructor(url: string) {
    this.url = url;

    this._onOpen = this._onOpen.bind(this);
    this._onMessage = this._onMessage.bind(this);
    this._onError = this._onError.bind(this);

    this.open();
  }

  private _onOpen() {
    this.status = CONNECTION_STATUS.OPEN;
    this.openHandle();
  }

  private _onMessage(event: MessageEvent) {
    try {
      // SSE 消息格式：data: {...}
      // 如果消息以 "data: " 开头，则解析 JSON
      let data = event.data;
      if (typeof data === 'string' && data.startsWith('data: ')) {
        data = data.substring(6); // 移除 "data: " 前缀
      }

      const message = JSON.parse(data);
      this.messageHandle(message);
    } catch (error) {
      // JSON 解析失败，尝试直接使用原始数据
      try {
        this.messageHandle(event.data);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[SSEClient] 消息解析失败:', error);
      }
    }
  }

  private _onError(event: Event) {
    this.status = CONNECTION_STATUS.CLOSED;
    this.errorHandle(event);
  }

  onMessage<T extends Record<string | number | symbol, unknown>>(callback: (message: T) => void) {
    this.messageHandle = callback;
  }

  onError(callback: (error: Event) => void) {
    this.errorHandle = callback;
  }

  onOpen(callback: () => void) {
    this.openHandle = callback;
  }

  onClose(_callback: () => void) {}

  offError() {
    this.errorHandle = () => {};
  }

  offOpen() {
    this.openHandle = () => {};
  }

  offClose() {}

  destroy() {
    this.close();
    // 移除所有事件监听器
    if (this.eventSource) {
      this.eventSource.removeEventListener('open', this._onOpen);
      this.eventSource.removeEventListener('message', this._onMessage);
      this.eventSource.removeEventListener('error', this._onError);
    }
    // 清空消息处理器
    this.messageHandle = () => {};
    this.errorHandle = () => {};
    this.openHandle = () => {};
  }

  open() {
    if (this.status !== CONNECTION_STATUS.CLOSED) {
      return this;
    }

    try {
      this.eventSource = new EventSource(this.url);
      this.status = CONNECTION_STATUS.CONNECTING;

      this.eventSource.addEventListener('open', this._onOpen);
      this.eventSource.addEventListener('message', this._onMessage);
      this.eventSource.addEventListener('error', this._onError);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[SSEClient] 创建 EventSource 失败:', error);
      this.status = CONNECTION_STATUS.CLOSED;
    }

    return this;
  }

  close() {
    if (this.status !== CONNECTION_STATUS.CLOSED && this.eventSource) {
      this.eventSource.close();
    }
    this.status = CONNECTION_STATUS.CLOSED;
  }

  // 添加自定义事件监听器
  addEventListener(event: string, callback: (event: MessageEvent) => void) {
    if (this.eventSource) {
      this.eventSource.addEventListener(event, callback);
    }
  }

  // 移除自定义事件监听器
  removeEventListener(event: string, callback: (event: MessageEvent) => void) {
    if (this.eventSource) {
      this.eventSource.removeEventListener(event, callback);
    }
  }
}

// ---------------------------------------------------------------------------
// PostSSEClient —— 支持 POST 请求的 SSE 客户端
// ---------------------------------------------------------------------------
// 标准 EventSource 仅支持 GET，PostSSEClient 通过 fetch + ReadableStream
// 实现 POST 请求下的 SSE 解析，提供与 SSEClient 类似的回调 API。

export type PostSSEEventCallback = (eventName: string, data: string) => void;

export interface PostSSEClientOptions {
  url: string;
  /** API 基础路径（同 axios baseURL），会拼接在 url 前面 */
  baseURL?: string;
  body: Record<string, unknown>;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  /**
   * 可选：返回 Authorization header 的值（如 "token 123 abc"）。
   * connect 内部会自动将其注入到请求 headers 中，调用方无需手动拼装。
   * 若返回 null / undefined 则跳过注入。
   */
  getAuthToken?: () => string | null | undefined;
}

export class PostSSEClient {
  private eventListeners: Map<string, Array<(data: string) => void>> = new Map();
  private catchAllListener: PostSSEEventCallback | null = null;
  private errorHandle: ((error: unknown) => void) | null = null;
  private doneHandle: (() => void) | null = null;

  /**
   * 监听特定名称的 SSE 事件
   *
   * ```ts
   * client.on('dialogue.init', (data) => { ... });
   * client.on('thread.message.delta', (data) => { ... });
   * ```
   */
  on(event: string, callback: (data: string) => void): this {
    const list = this.eventListeners.get(event) || [];
    list.push(callback);
    this.eventListeners.set(event, list);
    return this;
  }

  /** 监听所有事件（未被 on() 捕获的也会触发） */
  onEvent(callback: PostSSEEventCallback): this {
    this.catchAllListener = callback;
    return this;
  }

  /** 错误回调 */
  onError(callback: (error: unknown) => void): this {
    this.errorHandle = callback;
    return this;
  }

  /** 流结束回调 */
  onDone(callback: () => void): this {
    this.doneHandle = callback;
    return this;
  }

  /** 发起 POST 请求并开始接收 SSE 流 */
  async connect(options: PostSSEClientOptions): Promise<void> {
    const { url, baseURL = '', body, headers, signal, getAuthToken } = options;
    const fullUrl = `${baseURL}${url}`;

    // 自动注入 Authorization header（如果调用方提供了 getAuthToken）
    const authToken = getAuthToken?.();
    const authHeaders: Record<string, string> = authToken ? { Authorization: authToken } : {};
    // console.log(authHeaders);
    const response = await fetch(fullUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders,
        ...headers, // 显式传入的 headers 优先级更高，可覆盖 authHeaders
      },
      body: JSON.stringify(body),
      signal,
    });

    if (!response.ok) {
      const errMsg = `SSE request failed: ${response.status} ${response.statusText}`;
      this.errorHandle?.(new Error(errMsg));
      throw new Error(errMsg);
    }

    const reader = response.body?.getReader();
    if (!reader) {
      const err = new Error('Response body is not readable');
      this.errorHandle?.(err);
      throw err;
    }

    const decoder = new TextDecoder();
    let buffer = '';

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // 按双换行分割 SSE 事件块
        const parts = buffer.split('\n\n');
        buffer = parts.pop() ?? '';

        for (const part of parts) {
          if (!part.trim()) continue;
          this.parseSSEBlock(part);
        }
      }

      // 处理 buffer 中残留的最后一个事件
      if (buffer.trim()) {
        this.parseSSEBlock(buffer);
      }
    } catch (err: unknown) {
      if (err instanceof Error && err.name === 'AbortError') {
        // 被取消，不当错误处理
      } else {
        this.errorHandle?.(err);
        throw err;
      }
    } finally {
      this.doneHandle?.();
    }
  }

  /** 解析单个 SSE 事件块 */
  private parseSSEBlock(block: string): void {
    let eventName = 'message'; // 默认事件名
    const dataLines: string[] = [];

    for (const line of block.split('\n')) {
      if (line.startsWith('event:')) {
        eventName = line.slice(6).trim();
      } else if (line.startsWith('data:')) {
        dataLines.push(line.slice(5).trim());
      }
    }

    if (dataLines.length === 0) return;

    const data = dataLines.join('\n');

    // 触发具名监听器
    const listeners = this.eventListeners.get(eventName);
    if (listeners) {
      for (const cb of listeners) {
        cb(data);
      }
    }

    // 触发通用监听器
    this.catchAllListener?.(eventName, data);
  }
}
